import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getSecurityBalance,
  getSecurityBalanceSummary,
  getSecurityTransactions,
  createDeduction,
  createAdjustment,
  applyMonthlyDeduction,
  requestRefund,
  approveRefund,
  rejectRefund,
  type SecurityLedgerTxListParams,
} from '@/api/services/security-ledger.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  CreateDeductionBody,
  CreateAdjustmentBody,
  RequestRefundBody,
} from '@/features/security-ledger/types/security-ledger.types'

export const securityLedgerKeys = {
  all: (companyId: string | null) => ['security-ledger', companyId] as const,
  balance: (companyId: string | null, employeeId: string | null) =>
    ['security-ledger', companyId, employeeId, 'balance'] as const,
  balanceSummary: (companyId: string | null, employeeId: string | null) =>
    ['security-ledger', companyId, employeeId, 'balance-summary'] as const,
  /** Prefix for every transactions query of an employee, regardless of params. Use for invalidation. */
  transactionsScope: (companyId: string | null, employeeId: string | null) =>
    ['security-ledger', companyId, employeeId, 'transactions'] as const,
  transactions: (
    companyId: string | null,
    employeeId: string | null,
    params?: SecurityLedgerTxListParams,
  ) => ['security-ledger', companyId, employeeId, 'transactions', params] as const,
}

export function useSecurityBalance(
  companyId: string | null,
  employeeId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: securityLedgerKeys.balance(companyId, employeeId),
    queryFn: () => getSecurityBalance(companyId!, employeeId!),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && !!employeeId && options?.enabled !== false,
  })
}

export function useSecurityBalanceSummary(
  companyId: string | null,
  employeeId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: securityLedgerKeys.balanceSummary(companyId, employeeId),
    queryFn: () => getSecurityBalanceSummary(companyId!, employeeId!),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && !!employeeId && options?.enabled !== false,
  })
}

export function useSecurityTransactions(
  companyId: string | null,
  employeeId: string | null,
  params: SecurityLedgerTxListParams = { page: 1, limit: 10 },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: securityLedgerKeys.transactions(companyId, employeeId, params),
    queryFn: () => getSecurityTransactions(companyId!, employeeId!, params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && !!employeeId && options?.enabled !== false,
  })
}

function invalidateEmployee(queryClient: ReturnType<typeof useQueryClient>, companyId: string, employeeId: string) {
  queryClient.invalidateQueries({ queryKey: securityLedgerKeys.balance(companyId, employeeId) })
  queryClient.invalidateQueries({ queryKey: securityLedgerKeys.balanceSummary(companyId, employeeId) })
  queryClient.invalidateQueries({ queryKey: securityLedgerKeys.transactionsScope(companyId, employeeId) })
}

export function useCreateDeduction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreateDeductionBody }) =>
      createDeduction(companyId, body),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.body.employeeId)
      toast.success('Deduction posted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCreateAdjustment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreateAdjustmentBody }) =>
      createAdjustment(companyId, body),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.body.employeeId)
      toast.success('Adjustment posted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useApplyMonthlyDeduction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, employeeId }: { companyId: string; employeeId: string }) =>
      applyMonthlyDeduction(companyId, employeeId),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.employeeId)
      toast.success('Monthly deduction applied.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRequestRefund() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      employeeId,
      body,
    }: { companyId: string; employeeId: string; body: RequestRefundBody }) =>
      requestRefund(companyId, employeeId, body),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.employeeId)
      toast.success('Refund request submitted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useApproveRefund() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { companyId: string; txMongoId: string; employeeId: string }) =>
      approveRefund(vars.companyId, vars.txMongoId),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.employeeId)
      toast.success('Refund approved.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRejectRefund() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      companyId: string
      txMongoId: string
      employeeId: string
      reason?: string
    }) => rejectRefund(vars.companyId, vars.txMongoId, vars.reason),
    onSuccess: (_, variables) => {
      invalidateEmployee(queryClient, variables.companyId, variables.employeeId)
      toast.success('Refund rejected.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
